// src/components/tenant/kit.tsx — the portal's building blocks, the web twins of the phone app's ui/ (Card, HeroCard,
// ListGroup / ListRow, the gold foil Button, the action tiles). They only make sense under the `tenant` class that
// TenantShell sets (index.css › the tenant look); staff screens never use them.
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, type LucideIcon } from 'lucide-react';

/** The page's head: a small gold overline, the serif title, one muted line, and an action at the right. */
export function TPageHead({ overline, title, sub, action }: { overline?: ReactNode; title: ReactNode; sub?: ReactNode; action?: ReactNode }) {
  return (
    <div className="t-rise flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="min-w-0">
        {overline && <p className="t-overline mb-2">{overline}</p>}
        <h1 className="t-title">{title}</h1>
        {sub && <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-ink-500">{sub}</p>}
      </div>
      {action && <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div>}
    </div>
  );
}

/** A white card with the warm shadow. `title` sits in the card; `flush` drops the body padding (a list inside). */
export function TCard({ title, sub, action, children, className = '', flush = false, i }: { title?: ReactNode; sub?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; flush?: boolean; /** Stagger order for the rise-in. */ i?: number }) {
  return (
    <section className={`t-card t-rise ${className}`} style={i !== undefined ? ({ '--i': i } as React.CSSProperties) : undefined}>
      {(title || action) && (
        <div className={`flex items-start justify-between gap-3 px-5 pt-5 ${flush ? 'pb-2' : ''}`}>
          <div className="min-w-0">
            {title && <h2 className="text-[17px] font-semibold leading-snug tracking-[-0.01em] text-ink-900">{title}</h2>}
            {sub && <p className="mt-0.5 text-[13px] leading-relaxed text-ink-500">{sub}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      <div className={flush ? 'pb-2' : 'p-5'}>{children}</div>
    </section>
  );
}

/** A section heading between cards (the app's "Your building", "Notices"). */
export function TSection({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return <div className="flex items-end justify-between gap-3 pt-4"><h2 className="text-[20px] font-semibold tracking-[-0.015em] text-ink-900">{children}</h2>{action}</div>;
}

type RowProps = { icon?: LucideIcon; tone?: 'gold' | 'danger' | 'good' | 'sky'; title: ReactNode; sub?: ReactNode; trailing?: ReactNode; to?: string; href?: string; onClick?: () => void; chevron?: boolean };
const CHIP: Record<NonNullable<RowProps['tone']>, string> = {
  gold: 'bg-bronze-50 text-bronze-600',
  danger: 'bg-[#F0DED9] text-[#8A3F33]',
  good: 'bg-[#E4ECE0] text-[#3F6340]',
  sky: 'bg-[#E6F1F8] text-[#3C627E]',
};

/** One row of a list group: an icon chip, a title and a muted line, something at the right. 64px tall at least. */
export function TRow({ icon: I, tone = 'gold', title, sub, trailing, to, href, onClick, chevron }: RowProps) {
  const body = (
    <>
      {I && <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-full ${CHIP[tone]}`}><I className="h-[19px] w-[19px]" /></span>}
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold leading-snug text-ink-900">{title}</span>
        {sub && <span className="mt-0.5 block text-[13px] leading-snug text-ink-500">{sub}</span>}
      </span>
      {trailing && <span className="flex shrink-0 items-center gap-2">{trailing}</span>}
      {(chevron ?? !!(to || href || onClick)) && <ChevronRight className="h-4 w-4 shrink-0 text-ink-400" />}
    </>
  );
  const cls = 'flex min-h-[64px] w-full items-center gap-3.5 px-5 py-3 text-left';
  const hover = ' transition hover:bg-ink-50/80';
  if (to) return <li><Link to={to} className={cls + hover}>{body}</Link></li>;
  if (href) return <li><a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className={cls + hover}>{body}</a></li>;
  if (onClick) return <li><button type="button" onClick={onClick} className={cls + hover}>{body}</button></li>;
  return <li className={cls}>{body}</li>;
}

/** The rows of a list group, divided by a hairline. */
export function TList({ children }: { children: ReactNode }) {
  return <ul className="divide-y divide-ink-100">{children}</ul>;
}

/** A link styled as the portal's button: `foil` is the one main action, `soft` the cream secondary. */
export function TLinkButton({ to, href, children, variant = 'foil', icon: I, small, block, className = '' }: { to?: string; href?: string; children: ReactNode; variant?: 'foil' | 'soft'; icon?: LucideIcon; small?: boolean; block?: boolean; className?: string }) {
  const cls = `t-btn ${variant === 'foil' ? 't-btn-foil' : 't-btn-soft'} ${small ? 't-btn-sm' : ''} ${block ? 'w-full' : ''} ${className}`;
  const inner = <>{I && <I className="h-[18px] w-[18px]" />}{children}</>;
  if (href) return <a href={href} className={cls}>{inner}</a>;
  return <Link to={to ?? '#'} className={cls}>{inner}</Link>;
}

/** The hero card (the app's HeroCard): the cream-to-sand wash, the faint gold lotus, an overline, a big serif figure. */
export function THero({ overline, value, note, title, meta, alert, children }: { overline: ReactNode; value?: ReactNode; note?: ReactNode; title?: ReactNode; meta?: ReactNode; alert?: boolean; children?: ReactNode }) {
  return (
    <section className="t-hero t-rise p-6 sm:p-8">
      <img src="/lotus-mark.svg" alt="" aria-hidden className="pointer-events-none absolute -right-12 -top-12 h-56 w-56 opacity-[0.10]" draggable={false} />
      <p className="t-overline relative">{overline}</p>
      {title ? <h2 className="t-serif relative mt-3 max-w-[85%] text-[32px] leading-[1.1] text-ink-900 sm:text-[38px]">{title}</h2> : (
        <div className="relative mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="t-serif text-[50px] leading-none tracking-[-0.02em] text-ink-900 sm:text-[60px]">{value}</span>
          {note && <span className="flex items-center gap-2 text-[16px] text-ink-600">{note}{alert && <span className="h-2 w-2 rounded-full bg-[#A85A4C]" aria-hidden />}</span>}
        </div>
      )}
      {meta && <div className="relative mt-3 text-[15px] leading-relaxed text-ink-600">{meta}</div>}
      {children && <div className="relative mt-6">{children}</div>}
    </section>
  );
}

/** A square action tile (the app's four on Home). `soon` adds the "Soon" chip (it still opens its page when it has one). */
export function TTile({ label, icon: I, to, soon, i }: { label: string; icon: LucideIcon; to?: string; soon?: boolean; i?: number }) {
  const inner = (
    <>
      <span className="grid h-12 w-12 place-items-center rounded-full bg-bronze-50 text-bronze-600"><I className="h-[22px] w-[22px]" /></span>
      <span className="min-h-[2.4em] text-center text-[12.5px] font-semibold leading-[1.2] text-ink-900 sm:min-h-0 sm:text-[14px]">{label}</span>
      {soon && <span className="absolute right-1.5 top-1.5 rounded-full bg-ink-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-ink-600 sm:right-3 sm:top-3 sm:px-2 sm:text-[11px]">Soon</span>}
    </>
  );
  const cls = 't-card t-rise relative flex min-h-[116px] flex-col items-center justify-center gap-2 px-1.5 pb-2 pt-3 sm:min-h-[132px] sm:gap-2.5 sm:px-3';
  const style = i !== undefined ? ({ '--i': i } as React.CSSProperties) : undefined;
  if (!to) return <div className={`${cls} opacity-80`} style={style} aria-disabled="true" role="group" aria-label={soon ? `${label} — coming soon` : label}>{inner}</div>;
  return <Link to={to} aria-label={soon ? `${label} — coming soon` : undefined} className={`${cls} transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-float)] active:scale-[0.97]`} style={style}>{inner}</Link>;
}

/** A soft notice strip: good news, a warning, or the urgent line. */
export function TBanner({ tone = 'gold', icon: I, children }: { tone?: 'gold' | 'good' | 'danger' | 'sky'; icon?: LucideIcon; children: ReactNode }) {
  const look = { gold: 'bg-bronze-50 text-ink-800', good: 'bg-[#E4ECE0] text-[#2F4A2E]', danger: 'bg-[#F0DED9] text-[#6E2A20]', sky: 'bg-[#E6F1F8] text-[#213847]' }[tone];
  return <div className={`t-rise flex items-start gap-3 rounded-[16px] px-4 py-3.5 text-[14px] leading-relaxed ${look}`}>{I && <I className="mt-0.5 h-[18px] w-[18px] shrink-0" />}<div className="min-w-0">{children}</div></div>;
}

/** Label / value pairs, one per row (a tenancy, a booking). */
export function TFacts({ items }: { items: Array<{ k: ReactNode; v: ReactNode }> }) {
  return (
    <dl className="divide-y divide-ink-100">
      {items.map(({ k, v }, n) => <div key={n} className="flex items-start justify-between gap-4 py-3 text-[14px]"><dt className="shrink-0 text-ink-500">{k}</dt><dd className="min-w-0 text-right font-semibold text-ink-900">{v}</dd></div>)}
    </dl>
  );
}

/** An empty or error state inside the portal. */
export function TEmpty({ icon: I, title, body, action }: { icon: LucideIcon; title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="t-card t-rise px-6 py-12 text-center">
      <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-bronze-50 text-bronze-600"><I className="h-6 w-6" /></span>
      <p className="t-serif mt-4 text-[26px] leading-tight text-ink-900">{title}</p>
      {body && <p className="mx-auto mt-2 max-w-sm text-[14px] leading-relaxed text-ink-500">{body}</p>}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

/** A labelled form field in the portal: the label, the control, then the hint or the error under it. */
export function TField({ label, error, hint, children }: { label: string; error?: string | null; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-semibold text-ink-700">{label}</span>
      {children}
      {error ? <span role="alert" className="mt-1.5 block text-[13px] text-[#8A3F33]">{error}</span> : hint ? <span className="mt-1.5 block text-[12.5px] leading-relaxed text-ink-500">{hint}</span> : null}
    </label>
  );
}

/** The portal's text input: 48px tall, 16px type (no zoom on a phone), the gold focus ring. */
export function TInput({ invalid, className = '', ...props }: React.ComponentProps<'input'> & { invalid?: boolean }) {
  return (
    <input {...props} aria-invalid={invalid || undefined}
      className={`block min-h-[48px] w-full rounded-[14px] border bg-white px-3.5 text-[16px] text-ink-900 outline-none transition placeholder:text-ink-400 focus:border-bronze-500 disabled:bg-ink-50 disabled:text-ink-500 ${invalid ? 'border-[#A85A4C]' : 'border-ink-300'} ${className}`} />
  );
}
